import * as B from '@blueprintjs/core';
import * as Sentry from '@sentry/react';
import classnames from 'classnames';
import geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import {Link} from 'react-router-dom';

import MapOverlayDialog from 'app/components/MapOverlayDialog/MapOverlayDialog';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiDollarUnitUsage, ApiImageryContract} from 'app/modules/Remote/Project';
import {useImageryPrices} from 'app/providers/ImageryPricesProvider';
import * as C from 'app/utils/constants';
import * as CONSTANTS from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';
import {calculateCost, formatCost} from 'app/utils/costUtils';
import * as featureUtils from 'app/utils/featureUtils';
import {UTMArea} from 'app/utils/geoJsonUtils';
import {CachedApiGetterOptions, StatusMaybe, useTimestampMs} from 'app/utils/hookUtils';
import {formatCaptureDate, getLayerKeyForScene} from 'app/utils/imageryUtils';
import {LAYER_PACKAGES} from 'app/utils/layers';
import {getOrgPrefix} from 'app/utils/organizationUtils';
import * as userUtils from 'app/utils/userUtils';

import ImageryPreviewImage from './ImageryPreviewImage';
import {
  ContractDetailsDescription,
  ORDERABLE_SCENE_ERRORS,
  canPurchaseOnContract,
  formatAcres,
  getBillableAcres,
  getPackageOrderText,
  validateOrderableScene,
} from './orderImageryUtils';
import cs from './styles.styl';

export interface Props {
  orderableScene: I.ImmutableOf<ApiOrderableScene>;
  feature: I.ImmutableOf<ApiFeature>;
  organization: I.ImmutableOf<ApiOrganization>;
  role: ApiOrganizationUser['role'];
  modalUrl: string | null;

  disabledSources?: string[];
  blockedSources?: string[];

  getCurrentImageryContract: (
    opts: CachedApiGetterOptions
  ) => StatusMaybe<ApiImageryContract | null>;

  onClose: () => void;
  onSubmit: (billableAcres: number) => void;

  isOpen?: boolean;

  subsetFeature: geojson.Feature<geojson.Polygon> | null;
}

export const DollarUnitContractDetails: React.FunctionComponent<{
  usage: ApiDollarUnitUsage;
  acreage: number;
  layerKey: string;
  pricePerAcre: number;
  orderCents?: number; // If provided, this will override the cost calculation of pricePerAcre * acreage
}> = ({usage, acreage, layerKey, pricePerAcre, orderCents}) => {
  const orderableSceneType = layerKey in LAYER_PACKAGES ? 'package' : 'image';
  const usedCents = usage.purchased_cents;
  const currentOrderCents = orderCents ?? calculateCost(acreage, pricePerAcre);

  if (usage.nte_cents === null) {
    const remainingPrepaidCents = usage.prepaid_cents - usedCents;

    if (remainingPrepaidCents <= 0) {
      return (
        <p>
          Ordering this {orderableSceneType} costs <strong>{formatCost(currentOrderCents)}</strong>.
          By clicking “Order," you agree to this charge. Please note that orders cannot be refunded
          after purchase.
        </p>
      );
    } else if (remainingPrepaidCents >= currentOrderCents) {
      return (
        <p>
          Ordering this {orderableSceneType} will use{' '}
          <strong>{formatCost(currentOrderCents)}</strong> of the remaining prepaid{' '}
          <strong>{formatCost(remainingPrepaidCents)}</strong> for this portfolio. Please note that
          orders cannot be refunded after purchase.
        </p>
      );
    } else {
      return (
        <p>
          Ordering this {orderableSceneType} costs <strong>{formatCost(currentOrderCents)}</strong>.
          By clicking “Order,” you agree to use the remaining prepaid{' '}
          <strong>{formatCost(remainingPrepaidCents)}</strong> for this portfolio and a charge of{' '}
          <strong>{formatCost(Math.max(currentOrderCents - remainingPrepaidCents))}</strong> on your
          next invoice. Please note that orders cannot be refunded after purchase.
        </p>
      );
    }
  } else {
    const remainingCents = usage.nte_cents - usedCents;

    if (usedCents + currentOrderCents <= usage.nte_cents) {
      return (
        <p>
          Ordering this {orderableSceneType} will use{' '}
          <strong>{formatCost(currentOrderCents)}</strong> of the remaining{' '}
          <strong>{formatCost(remainingCents)}</strong> for this portfolio. Please note that orders
          cannot be refunded after purchase.
        </p>
      );
    } else if (remainingCents > 0) {
      return (
        <>
          <p>
            Ordering this {orderableSceneType} costs{' '}
            <strong>{formatCost(currentOrderCents)}</strong>, but you cannot order it because there
            is only <strong>{formatCost(remainingCents)}</strong> remaining for this portfolio.
          </p>

          <p>Please have an account administrator contact us for options.</p>
        </>
      );
    } else {
      return (
        <>
          <p>
            Ordering this {orderableSceneType} costs{' '}
            <strong>{formatCost(currentOrderCents)}</strong>, but you cannot order it because there
            are no funds remaining for this portfolio.
          </p>

          <p>Please have an account administrator contact us for options.</p>
        </>
      );
    }
  }
};

export const PURCHASE_DISABLED_SOURCES = CONSTANTS.DISABLE_ORDERS_SOURCE_IDS;
export const THUMBNAIL_BLOCKED_SOURCES = [];

const OrderImageryModal: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  feature,
  role,
  modalUrl,
  isOpen = true,
  onClose,
  onSubmit,
  orderableScene,
  getCurrentImageryContract,
  disabledSources = PURCHASE_DISABLED_SOURCES,
  blockedSources = THUMBNAIL_BLOCKED_SOURCES,
  subsetFeature,
  organization,
}) => {
  const [isFeatureHidden, setIsFeatureHidden] = React.useState<boolean>(false);

  const {url, sourceDetails} = featureUtils.imageAndSourceDetailsFromScene(orderableScene);
  const sourceId = orderableScene.get('sourceId');

  const {prices: imageryPrices} = useImageryPrices();
  const pricePerAcre: number | null = imageryPrices[sourceId];

  let sceneCoverage = 0;
  let sceneCoverageFailed = false;

  try {
    // This can raise an exeception if the geometry is weird (polygon has
    // duplicate points, or precision is too high leading to epsilon errors).
    sceneCoverage = featureUtils.getSceneCoverage(orderableScene, feature);
  } catch (e) {
    console.error(e);
    Sentry.captureException(e, {
      extra: {
        featureId: feature.get('id'),
        orderableSceneSource: sourceId,
        orderableSceneSensingTime: orderableScene.get('sensingTime'),
      },
    });
    sceneCoverageFailed = true;
  }

  const isPartialScene = sceneCoverage < 1;

  const {billableAcres, orderGeometryAcres} = getBillableAcres(
    feature,
    orderableScene,
    subsetFeature
  );

  const subsetFeatureAcres = React.useMemo(() => {
    return (
      subsetFeature &&
      Math.floor(conversionUtils.convert(UTMArea(subsetFeature), C.UNIT_AREA_M2, 'areaAcre'))
    );
  }, [subsetFeature]);

  const isDisabledSource = disabledSources.includes(sourceId);

  const [contractAfterMs, refreshContractAfterMs] = useTimestampMs();
  const imageryContractStatusMaybe = getCurrentImageryContract({
    afterMs: contractAfterMs,
    // We won't interrupt with a loading spinner if the last contract was
    // fetched within the last 60 seconds, but we will still fetch the latest.
    allowStaleMs: contractAfterMs - 60 * 1000,
  });

  // Guarantee that if we have an imagery contract it is of type dollarUnit. AcreUnit
  // contracts are no longer created and non are active. It still exists in the type
  // because we display info about expired contracts in the org settings
  const imageryContract =
    imageryContractStatusMaybe.value &&
    imageryContractStatusMaybe.value.contractType === CONSTANTS.CONTRACT_TYPE_DOLLAR_UNIT
      ? imageryContractStatusMaybe.value
      : null;

  const exceedsContractLimit =
    imageryContract && !canPurchaseOnContract(imageryContract, billableAcres, pricePerAcre);

  const orderableSceneValidationResult = validateOrderableScene(orderableScene.toJS());

  const isPartiallyOrdered =
    !orderableScene.get('isFullyOrdered') && orderableScene.get('isOrdered');
  const isOrderingSubset = !!subsetFeature;

  const sceneCloudCoverPercentage: number = orderableScene.get('cloudCoverPercentage');

  const layerKey = getLayerKeyForScene(orderableScene);
  const isPackage = layerKey in LAYER_PACKAGES;
  // Only show preview image for planet package if we are placing a partial order so
  // the user can see the area they are ordering. Otherwise the preview is unhelpful
  // since we have no image to show
  const showImageryPreview = !isPackage || isOrderingSubset;

  const orderExceedsMaximumOrderAcres =
    sourceDetails.maximum_order_acres !== undefined &&
    billableAcres > sourceDetails.maximum_order_acres;

  const canPurchase =
    !exceedsContractLimit &&
    !isDisabledSource &&
    !orderableSceneValidationResult.error &&
    !sceneCoverageFailed;

  const sourceName = sourceDetails.name;
  const sourceOperator = sourceDetails.operator;
  const sourceResolution = sourceDetails.resolution;

  const submitButtonText = React.useMemo(() => {
    let text = isOrderingSubset
      ? 'Order Subset'
      : isPartiallyOrdered
        ? 'Order Remaining Area'
        : 'Order';

    const currentOrderCents = calculateCost(billableAcres, pricePerAcre);
    text = `${text} for ${formatCost(currentOrderCents)}`;

    return text;
  }, [billableAcres, isOrderingSubset, isPartiallyOrdered, pricePerAcre]);

  const submitButton = (
    <B.AnchorButton
      intent={B.Intent.PRIMARY}
      large
      icon={isPartialScene ? 'warning-sign' : null}
      text={submitButtonText}
      disabled={!canPurchase}
      onClick={() => onSubmit(billableAcres)}
    />
  );

  let acresMessage = '';
  if (subsetFeature && subsetFeatureAcres === orderGeometryAcres) {
    // partial order that is fully contained within property
    acresMessage = `Your partial area selection, outlined in yellow, includes ${formatAcres(
      orderGeometryAcres
    )} acres within your property at ${formatCost(pricePerAcre)} per acre.`;
  } else if (subsetFeature) {
    // partial order whose shape extends outside of property lines
    acresMessage = `Your partial area selection, outlined in yellow, includes ${formatAcres(
      orderGeometryAcres
    )} new acres within your property at ${formatCost(pricePerAcre)} per acre.`;
  } else if (isPartiallyOrdered) {
    // ordering the rest of a scene that has been partially ordered
    acresMessage = `Your selection includes the remaining ${formatAcres(
      orderGeometryAcres
    )} acres available in this scene at ${formatCost(pricePerAcre)} per acre.`;
  } else {
    // standard order case
    acresMessage = `Your selection includes ${formatAcres(
      orderGeometryAcres
    )} acres at ${formatCost(pricePerAcre)} per acre.`;
  }

  if (orderGeometryAcres === billableAcres) {
    acresMessage += ' You will only be billed for the area in your property boundary.';
  }

  return (
    <B.Overlay isOpen={isOpen} hasBackdrop={true} onClose={onClose}>
      <MapOverlayDialog
        title="Order Confirmation"
        onClose={onClose}
        className={classnames(cs.dialog, {[cs.narrowDialog]: !showImageryPreview})}
        contentClassName={cs.dialogContent}
        additionalButtons={
          <>
            {!isPackage && (
              <B.Tooltip
                content={isFeatureHidden ? 'Hide boundary in preview' : 'Show boundary in preview'}
                position={B.Position.TOP}
              >
                <B.AnchorButton
                  icon={isFeatureHidden ? 'eye-open' : 'eye-off'}
                  minimal={true}
                  onClick={() => {
                    setIsFeatureHidden(!isFeatureHidden);
                  }}
                />
              </B.Tooltip>
            )}
            <B.Tooltip content="Copy link" position={B.Position.TOP}>
              <CopyToClipboard text={modalUrl}>
                <B.AnchorButton icon="clipboard" minimal={true} />
              </CopyToClipboard>
            </B.Tooltip>
          </>
        }
      >
        <div className={cs.content}>
          {imageryContractStatusMaybe.status === 'unknown' && (
            <B.Spinner className={cs.loadingSpinner} />
          )}
          {imageryContractStatusMaybe.status === 'error' && (
            <B.NonIdealState
              iconMuted={false}
              className={cs.errorMessage}
              icon="warning-sign"
              description="We weren’t able to load purchase information for this project right now."
              action={
                <B.Button intent="primary" onClick={() => refreshContractAfterMs()}>
                  Try Again
                </B.Button>
              }
            ></B.NonIdealState>
          )}

          {imageryContractStatusMaybe.status === 'some' &&
            imageryContract &&
            (billableAcres > 0 ? (
              <>
                <div className={cs.purchaseInfoContainer}>
                  <div className={cs.purchaseInfoDescription}>
                    <div>
                      {(canPurchase || isDisabledSource) && (
                        <>
                          {isPackage ? (
                            getPackageOrderText(layerKey)
                          ) : (
                            <>
                              <p>
                                This image was taken on{' '}
                                <strong>
                                  {formatCaptureDate(
                                    orderableScene.get('sensingTime'),
                                    `${orderableScene.get('sourceId')}_high-res-truecolor`,
                                    {showCaptureTime: true, descriptive: true}
                                  )}
                                </strong>
                                .
                                {sceneCloudCoverPercentage != undefined && (
                                  <span>
                                    {' '}
                                    It is part of a larger scene with{' '}
                                    <strong>{Math.round(sceneCloudCoverPercentage)}%</strong> cloud
                                    cover.
                                  </span>
                                )}
                              </p>
                              <p>
                                Please check the preview image for <strong>clouds</strong>,{' '}
                                <strong>coloration</strong>, <strong>contrast</strong>, and{' '}
                                <strong>coverage area</strong> to ensure that it meets your needs.
                              </p>
                              <p>
                                See our{' '}
                                <a
                                  href="https://support.upstream.tech/article/136-choosing-imagery"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  guidance for choosing imagery here
                                </a>
                                , or track imagery spending from the{' '}
                                <Link
                                  to={`/${getOrgPrefix(organization)}/settings/billing`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  billing page here
                                </Link>
                                .
                              </p>
                            </>
                          )}

                          {sourceId.startsWith('AIRBUS') && sourceId.endsWith('ARCHIVE') && (
                            <>
                              <hr className={cs.separator} />
                              <B.Callout icon={null} intent={B.Intent.DANGER}>
                                <p>
                                  <strong>Warning:</strong> Imagery from the Extended Archive may
                                  take up to 72 hours to process, and quality can vary. Please
                                  exercise caution and carefully review the preview for any
                                  potential issues, including cloud cover, discoloration, and
                                  geographical inaccuracies.
                                </p>
                              </B.Callout>
                            </>
                          )}
                          <hr className={cs.separator} />
                        </>
                      )}
                      <ContractDetailsDescription
                        isDisabledSource={isDisabledSource}
                        isProcessing={
                          orderableSceneValidationResult.error ===
                          ORDERABLE_SCENE_ERRORS.ORDER_STILL_PROCESSING
                        }
                        sceneCoverageFailed={sceneCoverageFailed}
                        canPurchase={canPurchase}
                        layerKey={getLayerKeyForScene(orderableScene)}
                        sceneSensingTime={orderableScene.get('sensingTime')}
                        orderGeometryAcres={orderGeometryAcres}
                        billableAcres={billableAcres}
                        imageryContract={imageryContract}
                        acresMessage={acresMessage}
                        orderExceedsMaximumOrderAcres={orderExceedsMaximumOrderAcres}
                        role={role}
                      />
                    </div>
                  </div>

                  <div className={cs.purchaseInfoActions}>
                    {userUtils.mayOrderImagery(role) ? (
                      <>
                        {isPartialScene ? (
                          <B.Tooltip
                            content={
                              <>
                                <strong>Note:</strong> This data does not fully cover the property
                              </>
                            }
                          >
                            {submitButton}
                          </B.Tooltip>
                        ) : subsetFeature ? (
                          <B.Tooltip
                            content={
                              <>
                                <strong>Note:</strong> This is a partial area order
                              </>
                            }
                          >
                            {submitButton}
                          </B.Tooltip>
                        ) : (
                          submitButton
                        )}
                        <B.Button large text="Cancel" minimal onClick={onClose} />
                      </>
                    ) : (
                      <CopyToClipboard text={modalUrl}>
                        <B.Button intent={B.Intent.PRIMARY} large text="Copy Link" type="submit" />
                      </CopyToClipboard>
                    )}
                  </div>
                </div>

                {showImageryPreview && (
                  <div className={cs.previewArea}>
                    <div className={cs.previewImageContainer}>
                      <ImageryPreviewImage
                        feature={feature}
                        maxWidth={420}
                        maxHeight={420}
                        url={isPackage ? undefined : url}
                        bounds={orderableScene.get('thumbnailBbox').toJS()}
                        sourceId={sourceId}
                        sceneGeometry={orderableScene.get('sceneGeometry').toJS()}
                        blockedSources={blockedSources}
                        isFeatureHidden={isFeatureHidden}
                        subsetFeature={subsetFeature}
                        orderedGeometry={orderableScene.get('orderedGeometry')?.toJS()}
                      />
                    </div>
                    <div className={cs.previewDescription}>
                      <div>
                        {sourceOperator} {sourceName}
                        {sourceResolution && (
                          <>
                            {' '}
                            (<span className={cs.sourceResolution}>{sourceResolution}m</span>)
                          </>
                        )}
                      </div>
                      <div className={cs.previewDisclaimer}>
                        This is a low-resolution preview. It will be high-resolution when processed.
                        <br />
                        {isPartialScene ? (
                          <>
                            <strong style={{color: '#cc0000'}}>Red</strong> areas show where the
                            image does not cover the property.
                          </>
                        ) : (
                          'Please check for clouds and that the image covers the full property boundary.'
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <B.NonIdealState
                iconMuted={false}
                className={cs.errorMessage}
                icon="warning-sign"
                description={
                  <>
                    <p>
                      We weren’t able to find orderable data. It may be that you have already
                      ordered all the data that covers your property.
                    </p>
                    <p>
                      If parts of your property are available but missing from data you’ve already
                      ordered, contact <strong>lens@upstream.tech</strong> for support.
                    </p>
                  </>
                }
              />
            ))}
        </div>
      </MapOverlayDialog>
    </B.Overlay>
  );
};

export default OrderImageryModal;
